import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPublicBlogs } from '../../../utils/api';
import Icon from '../../../components/AppIcon';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';

const BlogsSection = () => {
  const { isEnabled } = useFeatureFlags();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const data = await getPublicBlogs();
        setBlogs((data || []).slice(0, 3));
      } catch (error) {
        console.error('Failed to fetch latest blogs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  if (loading || blogs.length === 0 || !isEnabled('blog')) return null;

  return (
    <section className="border-t border-[var(--lp-line)] bg-[var(--lp-bg)] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-mono text-[11px] font-semiboldtracking-[0.2em] text-[var(--lp-accent)]">
              From the blog
            </p>
            <h2 className="mt-3 font-semibold text-4xl text-[var(--lp-ink)] sm:text-5xl">
              Interview craft, written down.
            </h2>
          </div>
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 font-mono text-xs font-semiboldtracking-wider text-[var(--lp-ink)] transition-colors hover:text-[var(--lp-accent)]"
          >
            All articles
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {blogs.map((blog) => (
            <Link
              key={blog.id}
              to={`/blogs/${blog.slug}`}
              className="group flex flex-col overflow-hidden rounded-2xl border border-[var(--lp-line)] bg-[var(--lp-surface)] transition-all hover:border-[var(--lp-accent)]/40 hover:shadow-[0_20px_50px_-20px_var(--lp-accent-glow)]"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-[var(--lp-line)]">
                {blog.cover_image_url ? (
                  <img
                    src={blog.cover_image_url}
                    alt={blog.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center text-[var(--lp-ink-soft)]">
                    <Icon name="Image" size={32} className="opacity-40" />
                  </div>
                )}
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="mb-3 flex items-center gap-3 font-mono text-[10px]tracking-wider text-[var(--lp-ink-soft)]">
                  <span>
                    {new Date(blog.created_at || Date.now()).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                  <span>{blog.read_time_minutes || 3} min read</span>
                </div>
                <h3 className="font-semibold text-xl leading-snug text-[var(--lp-ink)] line-clamp-2 group-hover:text-[var(--lp-accent)] transition-colors">
                  {blog.title}
                </h3>
                <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-[var(--lp-ink-soft)]">
                  {blog.summary}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[var(--lp-accent)]">
                  Read
                  <Icon name="ArrowRight" size={14} className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogsSection;
